import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DEFAULT_UNIT, normaliseTags, type InventoryCategory, type InventoryItem, type InventoryLocation } from '@jupi/shared'
import { categoryName } from './labels'
import { EMPTY_LOT, LotFields, draftToLot, lotProblem, type LotDraft } from './LotFields'
import type { FleetAircraft, ItemBody } from './queries'

interface ItemDraft {
  categoryKey: string
  name: string
  description: string
  partNumber: string
  manufacturer: string
  unit: string
  minQuantity: string
  tags: string[]
  notes: string
  fitRegistrations: string[]
  fitTypes: string[]
}

function toDraft(item: InventoryItem | null, categoryKey: string): ItemDraft {
  return {
    categoryKey: item?.categoryKey ?? categoryKey,
    name: item?.name ?? '',
    description: item?.description ?? '',
    partNumber: item?.partNumber ?? '',
    manufacturer: item?.manufacturer ?? '',
    unit: item?.unit ?? DEFAULT_UNIT,
    minQuantity: item?.minQuantity == null ? '' : String(item.minQuantity),
    tags: item?.tags ?? [],
    notes: item?.notes ?? '',
    fitRegistrations: item?.fitments.flatMap((f) => (f.registration ? [f.registration] : [])) ?? [],
    fitTypes: item?.fitments.flatMap((f) => (f.aircraftType ? [f.aircraftType] : [])) ?? [],
  }
}

// Add or edit an item. Adding can also record the first stock lot, so "I just
// bought three oil filters" is one form.
export function ItemDialog({
  open,
  item,
  defaultCategoryKey,
  categories,
  locations,
  aircraft,
  knownTags,
  otherItems,
  saving,
  onSave,
  onClose,
  language,
}: {
  open: boolean
  item: InventoryItem | null
  defaultCategoryKey: string
  categories: InventoryCategory[]
  locations: InventoryLocation[]
  aircraft: FleetAircraft[]
  knownTags: string[]
  // Used only to warn about a part number already in use.
  otherItems: Pick<InventoryItem, 'itemId' | 'partNumber' | 'name'>[]
  saving: boolean
  onSave: (body: ItemBody) => void
  onClose: () => void
  language: string
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<ItemDraft>(() => toDraft(item, defaultCategoryKey))
  const [withLot, setWithLot] = useState(true)
  const [lot, setLot] = useState<LotDraft>(EMPTY_LOT)
  useEffect(() => {
    if (!open) return
    setDraft(toDraft(item, defaultCategoryKey))
    setLot(EMPTY_LOT)
    setWithLot(true)
  }, [open, item, defaultCategoryKey])

  const category = categories.find((c) => c.categoryKey === draft.categoryKey)
  const aircraftRelated = category?.aircraftRelated ?? false
  const types = useMemo(() => [...new Set(aircraft.flatMap((a) => (a.aircraftType ? [a.aircraftType] : [])))].sort(), [aircraft])
  const set = (key: keyof ItemDraft) => (e: React.ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, [key]: e.target.value }))

  const part = draft.partNumber.trim().toLowerCase()
  const duplicate = part !== '' && otherItems.some((o) => o.itemId !== item?.itemId && o.partNumber?.trim().toLowerCase() === part)
  const minQuantity = draft.minQuantity.trim()
  const minInvalid = minQuantity !== '' && !(Number.isInteger(Number(minQuantity)) && Number(minQuantity) >= 0)
  const lotInvalid = !item && withLot && lotProblem(lot) !== null
  const valid = draft.name.trim() !== '' && draft.categoryKey !== '' && !minInvalid && !lotInvalid

  const submit = () => {
    const fitments = aircraftRelated
      ? [...draft.fitRegistrations.map((registration) => ({ registration })), ...draft.fitTypes.map((aircraftType) => ({ aircraftType }))]
      : []
    onSave({
      categoryKey: draft.categoryKey,
      name: draft.name.trim(),
      description: draft.description.trim(),
      partNumber: draft.partNumber.trim() || null,
      manufacturer: draft.manufacturer.trim(),
      unit: draft.unit.trim() || DEFAULT_UNIT,
      minQuantity: minQuantity === '' ? null : Number(minQuantity),
      tags: normaliseTags(draft.tags),
      notes: draft.notes.trim(),
      fitments,
      ...(!item && withLot ? { lot: draftToLot(lot) } : {}),
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t(item ? 'inventory.edit' : 'inventory.add')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label={t('inventory.category')} value={draft.categoryKey} onChange={set('categoryKey')}>
            {categories.map((c) => (
              <MenuItem key={c.categoryKey} value={c.categoryKey}>
                {categoryName(c, language)}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('inventory.name')} value={draft.name} onChange={set('name')} required autoFocus />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('inventory.partNumber')}
              value={draft.partNumber}
              onChange={set('partNumber')}
              sx={{ flex: 1 }}
              error={duplicate}
              helperText={duplicate ? t('inventory.duplicatePartNumber') : undefined}
            />
            <TextField label={t('inventory.manufacturer')} value={draft.manufacturer} onChange={set('manufacturer')} sx={{ flex: 1 }} />
          </Stack>
          <TextField label={t('inventory.description')} value={draft.description} onChange={set('description')} multiline minRows={2} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('inventory.unit')} value={draft.unit} onChange={set('unit')} sx={{ flex: 1 }} />
            <TextField
              label={t('inventory.minQuantity')}
              helperText={t('inventory.minQuantityHelp')}
              type="number"
              value={draft.minQuantity}
              onChange={set('minQuantity')}
              error={minInvalid}
              slotProps={{ htmlInput: { min: 0, step: 1, inputMode: 'numeric' } }}
              sx={{ flex: 2 }}
            />
          </Stack>
          <Autocomplete
            multiple
            freeSolo
            options={knownTags}
            value={draft.tags}
            onChange={(_, tags) => setDraft((d) => ({ ...d, tags: tags as string[] }))}
            renderInput={(params) => <TextField {...params} label={t('inventory.tags')} helperText={t('inventory.tagsHelp')} />}
          />
          {aircraftRelated ? (
            <Stack spacing={2}>
              <Typography variant="subtitle2">{t('inventory.fits')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: '-8px !important' }}>
                {t('inventory.fitsHelp')}
              </Typography>
              <Autocomplete
                multiple
                options={aircraft.map((a) => a.registration)}
                value={draft.fitRegistrations}
                onChange={(_, fitRegistrations) => setDraft((d) => ({ ...d, fitRegistrations }))}
                renderInput={(params) => <TextField {...params} label={t('inventory.fitsRegistration')} />}
              />
              <Autocomplete
                multiple
                freeSolo
                options={types}
                value={draft.fitTypes}
                onChange={(_, fitTypes) => setDraft((d) => ({ ...d, fitTypes: fitTypes as string[] }))}
                renderInput={(params) => <TextField {...params} label={t('inventory.fitsType')} />}
              />
            </Stack>
          ) : null}
          <TextField label={t('inventory.notes')} value={draft.notes} onChange={set('notes')} multiline minRows={2} />
          {!item ? (
            <>
              <FormControlLabel
                control={<Checkbox checked={withLot} onChange={(e) => setWithLot(e.target.checked)} />}
                label={t('inventory.firstLot')}
              />
              {withLot ? <LotFields draft={lot} onChange={setLot} locations={locations} showSerial={aircraftRelated} /> : null}
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={submit}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
